import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { FileText, AlertCircle, Plus, Trash2, Wrench } from "lucide-react";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { formatCurrency, formatDate } from "../../lib/format";
// 29/09/2026 — lista CANONICA dei tipi polizza, la stessa del "crea polizza":
// il menu a tendina dell'import non duplica piu' le opzioni.
import { POLICY_TYPES, getPolicyTypeInfo } from "../policies";

/**
 * PolicyDataCard - Displays and edits policy information
 * Used in PDF Import, Email Import
 */
export function PolicyDataCard({ 
  policyData, 
  isEditing, 
  onUpdateField,
  companySuggestions = [],
  fieldConfidence = {},
  servicesCatalog = [],
  onAddService,
  onRemoveService
}) {
  const fc = (field) => fieldConfidence[`policy_${field}`];
  
  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <FileText className="h-5 w-5 text-green-500" />
          Dati Polizza
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isEditing ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <PolicyFieldLabel label="N° Polizza" confidence={fc("policy_number")} />
              <Input
                value={policyData?.policy_number || ""}
                onChange={(e) => onUpdateField("policy_number", e.target.value)}
                data-testid="policy-number-input"
              />
            </div>
            <div>
              <PolicyFieldLabel label="Compagnia" confidence={fc("company")} />
              <Input
                value={policyData?.company || ""}
                onChange={(e) => onUpdateField("company", e.target.value.toUpperCase())}
                className="uppercase"
                data-testid="policy-company-input"
              />
              {/* Suggerimenti compagnie simili */}
              {companySuggestions && companySuggestions.length > 0 && (
                <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md">
                  <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 mb-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>Compagnie simili nel tuo database:</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {companySuggestions.map((sug, idx) => (
                      <Badge 
                        key={idx}
                        variant="outline"
                        className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors text-xs"
                        onClick={() => onUpdateField("company", sug.name)}
                      >
                        {sug.name}
                        {sug.match_type === "exact" && " ✓"}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div>
              <PolicyFieldLabel label="Tipo" confidence={fc("policy_type")} />
              {/* 29/09/2026 — prima era un campo libero: ora si SCEGLIE da un
                  menu a tendina con la STESSA lista del "crea polizza"
                  (components/policies.POLICY_TYPES). Il valore suggerito
                  dall'AI resta selezionato come proposta di default. */}
              <Select
                value={String(policyData?.policy_type || "").trim().toLowerCase()}
                onValueChange={(value) => onUpdateField("policy_type", value)}
              >
                <SelectTrigger data-testid="policy-type-input">
                  <SelectValue placeholder="Seleziona tipo">
                    {policyData?.policy_type && (
                      <span className="flex items-center gap-2">
                        <span>{getPolicyTypeInfo(policyData.policy_type).icon}</span>
                        <span>{getPolicyTypeInfo(policyData.policy_type).label}</span>
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {POLICY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <PolicyFieldLabel label="Premio di Polizza" confidence={fc("premium")} />
              <Input
                type="number"
                value={policyData?.premium || ""}
                onChange={(e) => onUpdateField("premium", e.target.value)}
                data-testid="policy-premium-input"
              />
            </div>
            <div>
              <PolicyFieldLabel label="Data Inizio" confidence={fc("start_date")} />
              <Input
                type="date"
                value={policyData?.start_date || ""}
                onChange={(e) => onUpdateField("start_date", e.target.value)}
                data-testid="policy-start-date-input"
              />
            </div>
            <div>
              <PolicyFieldLabel label="Data Scadenza" confidence={fc("expiry_date")} />
              <Input
                type="date"
                value={policyData?.expiry_date || ""}
                onChange={(e) => onUpdateField("expiry_date", e.target.value)}
                data-testid="policy-expiry-date-input"
              />
            </div>
            <div>
              <PolicyFieldLabel label="Frequenza Pagamento" />
              <Select
                value={policyData?.payment_frequency || "annual"}
                onValueChange={(value) => {
                  onUpdateField("payment_frequency", value);
                  if (value === "annual") {
                    onUpdateField("semiannual_expiry", "");
                  }
                }}
              >
                <SelectTrigger data-testid="policy-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="annual">Annuale</SelectItem>
                  <SelectItem value="semiannual">Semestrale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {policyData?.payment_frequency === "semiannual" && (
              <div>
                <PolicyFieldLabel label="Scadenza Rata Semestrale" />
                <Input
                  type="date"
                  value={policyData?.semiannual_expiry || ""}
                  onChange={(e) => onUpdateField("semiannual_expiry", e.target.value)}
                  data-testid="policy-semiannual-input"
                />
              </div>
            )}
            <div className="col-span-2">
              <PolicyFieldLabel label="Targa" confidence={fc("license_plate")} />
              <Input
                value={policyData?.license_plate || ""}
                onChange={(e) => onUpdateField("license_plate", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7))}
                className="uppercase"
                maxLength={7}
                placeholder="AB123CD"
                data-testid="policy-plate-input"
              />
            </div>
            <div className="col-span-2">
              <PolicyFieldLabel label="Note" />
              <Input
                value={policyData?.notes || ""}
                onChange={(e) => onUpdateField("notes", e.target.value)}
                placeholder="Massimali, franchigie, garanzie..."
                data-testid="policy-notes-input"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <PolicyField label="N° Polizza" value={policyData?.policy_number} mono bold confidence={fc("policy_number")} />
            <PolicyField label="Compagnia" value={policyData?.company} confidence={fc("company")} />
            <PolicyField label="Tipo" value={getPolicyTypeInfo(policyData?.policy_type).label} capitalize confidence={fc("policy_type")} />
            <PolicyField 
              label="Premio di Polizza" 
              value={policyData?.premium ? `€${policyData.premium}` : null} 
              highlight="green"
              confidence={fc("premium")}
            />
            <PolicyField label="Decorrenza" value={formatDate(policyData?.start_date)} confidence={fc("start_date")} />
            <PolicyField 
              label="Scadenza" 
              value={formatDate(policyData?.expiry_date)} 
              highlight="red" 
              bold
              confidence={fc("expiry_date")}
            />
            <PolicyField 
              label="Frequenza" 
              value={policyData?.payment_frequency === "semiannual" ? "Semestrale" : "Annuale"} 
              highlight={policyData?.payment_frequency === "semiannual" ? "orange" : null}
              bold={policyData?.payment_frequency === "semiannual"}
            />
            {policyData?.payment_frequency === "semiannual" && policyData?.semiannual_expiry && (
              <PolicyField 
                label="Scadenza Semestrale" 
                value={formatDate(policyData.semiannual_expiry)} 
                highlight="orange" 
                bold 
              />
            )}
            {policyData?.license_plate && (
              <PolicyField label="Targa" value={policyData.license_plate} mono confidence={fc("license_plate")} />
            )}
          </div>
        )}
        
        {policyData?.notes && (
          <div className="mt-3 p-3 bg-muted rounded-lg text-xs">
            <p className="text-muted-foreground font-medium mb-1">Note:</p>
            <p>{policyData.notes}</p>
          </div>
        )}

        {/* Additional Services Section */}
        <div className="mt-4 border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-medium flex items-center gap-1.5">
              <Wrench className="h-3.5 w-3.5 text-blue-500" />
              Servizi Aggiuntivi
            </Label>
          </div>

          {/* Current services list */}
          {policyData?.additional_services?.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {policyData.additional_services.map((svc, idx) => (
                <div key={idx} className="flex items-center justify-between bg-muted/50 rounded-md px-2 py-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{svc.provider}</span>
                    <span className="text-muted-foreground">{svc.description}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {svc.cost > 0 && (
                      <span className="text-green-600 font-mono text-xs">{formatCurrency(svc.cost)}</span>
                    )}
                    {isEditing && onRemoveService && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-red-500 hover:text-red-700 hover:bg-red-100"
                        onClick={() => onRemoveService(idx)}
                        data-testid={`remove-service-${idx}`}
                       aria-label="Rimuovi">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add service from catalog */}
          {isEditing && servicesCatalog.length > 0 && onAddService && (
            <div className="flex items-center gap-2">
              <Select
                onValueChange={(serviceId) => {
                  const svc = servicesCatalog.find(s => s.service_id === serviceId);
                  if (svc) {
                    onAddService({
                      provider: svc.provider,
                      description: svc.description,
                      cost: svc.default_cost || 0
                    });
                  }
                }}
                data-testid="add-service-select"
              >
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="add-service-trigger">
                  <SelectValue placeholder="Aggiungi servizio..." />
                </SelectTrigger>
                <SelectContent>
                  {servicesCatalog.map((svc) => (
                    <SelectItem key={svc.service_id} value={svc.service_id}>
                      {svc.provider} - {svc.description} {svc.default_cost > 0 ? `(${formatCurrency(svc.default_cost)})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isEditing && servicesCatalog.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              Nessun servizio nel catalogo. Crea servizi dalla sezione Impostazioni.
            </p>
          )}

          {!isEditing && (!policyData?.additional_services || policyData.additional_services.length === 0) && (
            <p className="text-xs text-muted-foreground">Nessun servizio aggiuntivo</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Helper: Label with confidence badge for edit mode
function PolicyFieldLabel({ label, confidence }) {
  return (
    <Label className="text-xs flex items-center gap-1.5">
      {label}
      <ConfidenceBadge level={confidence} />
    </Label>
  );
}

// Helper component for displaying a single field
function PolicyField({ label, value, mono = false, bold = false, capitalize = false, highlight = null, confidence }) {
  const valueClass = [
    mono && "font-mono",
    bold && "font-medium",
    capitalize && "capitalize",
    highlight === "green" && "text-green-600",
    highlight === "red" && "text-red-600",
    highlight === "orange" && "text-orange-600",
    !value && "text-muted-foreground"
  ].filter(Boolean).join(" ");

  return (
    <div className="flex justify-between items-center">
      <span className="text-muted-foreground flex items-center gap-1.5">
        {label}:
        <ConfidenceBadge level={confidence} />
      </span>
      <span className={valueClass}>{value || "-"}</span>
    </div>
  );
}

export default PolicyDataCard;
